import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Share2, Palette, Clapperboard, Camera, Package, Globe, Users, TrendingUp, ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

const ICONS = { Megaphone, Share2, Palette, Clapperboard, Camera, Package, Globe, Users, TrendingUp };

const CARD_THEMES = [
  { bg: "bg-gradient-to-br from-pink-50 to-rose-100",    border: "border-pink-200",    iconBg: "bg-pink-100",    iconColor: "text-[#FF007F]",   check1: "text-[#FF007F]",   check2: "text-rose-400" },
  { bg: "bg-gradient-to-br from-orange-50 to-amber-100", border: "border-orange-200",  iconBg: "bg-orange-100",  iconColor: "text-[#FF7F00]",   check1: "text-[#FF7F00]",   check2: "text-amber-400" },
  { bg: "bg-gradient-to-br from-cyan-50 to-sky-100",     border: "border-cyan-200",    iconBg: "bg-cyan-100",    iconColor: "text-[#00BFE0]",   check1: "text-[#00BFE0]",   check2: "text-sky-400" },
  { bg: "bg-gradient-to-br from-violet-50 to-purple-100",border: "border-violet-200",  iconBg: "bg-violet-100",  iconColor: "text-[#9B00FF]",   check1: "text-[#9B00FF]",   check2: "text-purple-400" },
  { bg: "bg-gradient-to-br from-emerald-50 to-green-100",border: "border-emerald-200", iconBg: "bg-emerald-100", iconColor: "text-emerald-600", check1: "text-emerald-500", check2: "text-green-400" },
  { bg: "bg-gradient-to-br from-blue-50 to-indigo-100",  border: "border-blue-200",    iconBg: "bg-blue-100",    iconColor: "text-blue-600",    check1: "text-blue-500",    check2: "text-indigo-400" },
];

const SERVICE_IMAGES = {
  'digital-marketing': 'https://images.unsplash.com/photo-1611926653458-09294b3142bf?w=900',
  'social-media': 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=900',
  'branding': 'https://images.unsplash.com/photo-1561070791-2526d30994b8?w=900',
  'production': 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=900',
  'studio-rental': 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=900',
  'props-rental': 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=900',
  'websites': 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=900',
  'influencer': 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=900',
  'growth': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900',
};

export default function Services() {
  const [services, setServices] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data)); }, []);

  return (
    <div data-testid="services-page" className="pt-24">
      <section className="container mx-auto px-4 pt-16 pb-12">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">Services</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none mb-6 max-w-4xl">
          End-to-end <span className="text-gradient-animate">brand & growth</span> solutions.
        </h1>
        <p className="text-lg text-[#52525B] max-w-2xl">
          Branding, ads, content, websites, studios — engineered to make your brand visible,
          loved and profitable.
        </p>
      </section>

      <section className="container mx-auto px-4 pb-24 space-y-6">
        {services.map((s, idx) => {
          const Icon = ICONS[s.icon] || Sparkles;
          const img = SERVICE_IMAGES[s.id] || 'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=900';
          const isEven = idx % 2 === 1;
          const half = Math.ceil(s.items.length / 2);
          const col1 = s.items.slice(0, half);
          const col2 = s.items.slice(half);
          return (
            <div key={s.id} data-testid={`service-${s.id}`}
              className={`flex flex-col ${isEven ? 'lg:flex-row-reverse' : 'lg:flex-row'} rounded-2xl overflow-hidden border ${CARD_THEMES[idx % CARD_THEMES.length].border} shadow-sm hover:shadow-lg transition-shadow duration-300`}>
              {/* Image */}
              <div className="lg:w-[42%] shrink-0 relative overflow-hidden min-h-[260px]">
                <img src={img} alt={s.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-xs font-mono uppercase tracking-widest text-white/80 bg-black/40 px-3 py-1 rounded-full backdrop-blur-sm">
                    {s.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className={`flex-1 p-8 lg:p-10 ${CARD_THEMES[idx % CARD_THEMES.length].bg} flex flex-col justify-between`}>
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl ${CARD_THEMES[idx % CARD_THEMES.length].iconBg} grid place-items-center`}>
                      <Icon className={`w-5 h-5 ${CARD_THEMES[idx % CARD_THEMES.length].iconColor}`} />
                    </div>
                    <h3 className="font-display font-black text-2xl lg:text-3xl text-[#0A1128]">{s.title}</h3>
                  </div>
                  <p className="text-[#52525B] mb-6 text-base">{s.tagline}</p>

                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-600 mb-3">What we offer</div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 mb-6">
                    {col1.map((it) => (
                      <div key={it} className="flex items-center gap-2 text-sm text-zinc-800 font-medium">
                        <CheckCircle2 className={`w-4 h-4 ${CARD_THEMES[idx % CARD_THEMES.length].check1} shrink-0`} /> {it}
                      </div>
                    ))}
                    {col2.map((it) => (
                      <div key={it} className="flex items-center gap-2 text-sm text-zinc-800 font-medium">
                        <CheckCircle2 className={`w-4 h-4 ${CARD_THEMES[idx % CARD_THEMES.length].check2} shrink-0`} /> {it}
                      </div>
                    ))}
                  </div>
                </div>

                <Link to="/quote"
                  className={`inline-flex items-center gap-2 text-sm font-bold ${CARD_THEMES[idx % CARD_THEMES.length].iconColor} hover:gap-3 transition-all group w-fit`}>
                  Get a quote <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          );
        })}
      </section>

      <section className="bg-[#0A1128] text-white py-24">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <Sparkles className="w-10 h-10 text-[#FF7F00] mx-auto mb-4" />
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mb-5">
            Not sure where to start?
          </h2>
          <p className="text-white/70 mb-8">
            Use our AI Quote Generator — answer a few questions and get a tailored proposal in 30 seconds.
          </p>
          <Link to="/quote" data-testid="services-cta-quote"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-brand text-white font-bold">
            Generate AI Quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
